/**
 * Où le bandeau cookies s'affiche, et où l'enregistrement des sessions a le droit de tourner.
 * Fonctions pures (testées dans consentScope.test.ts).
 */
import { CODE_GAMEPAD_URL } from '~/config/endpoints.config';
import { isExcludedClient } from '~/config/playerLink.config';

export type ScopeInput = { path: string; clientID?: string; mode?: string };

const lower = (p: string) => p.toLowerCase();
const under = (path: string, prefix: string) => {
    const p = lower(path);
    return p === prefix || p.startsWith(`${prefix}/`);
};

/** Pages des joueurs (téléphones des clients du bar) : jamais de bandeau, jamais d'enregistrement. */
export function isPlayerPath(path: string): boolean {
    return under(path, `/${lower(CODE_GAMEPAD_URL)}`) || under(path, '/reward');
}

/**
 * Bars et animateurs seulement : jamais un écran d'aéroport (mode auto, personne pour cliquer et
 * des voyageurs filmés), jamais un client sous contrat ni une démo (même liste que le lien joueurs).
 */
export function isEligibleClient(clientID: string | undefined, mode: string | undefined): boolean {
    if (!clientID || mode === 'auto') return false;
    return !isExcludedClient(clientID);
}

export function analyticsAllowed({ path, clientID, mode }: ScopeInput): boolean {
    return !isPlayerPath(path) && isEligibleClient(clientID, mode);
}

/**
 * Phase 1 : bandeau seulement sur les pages de l'animateur déjà inscrit, hors partie en cours
 * (`/app/…` = un jeu tourne sur la TV devant les clients du bar : jamais de bandeau par-dessus).
 */
const BANNER_PREFIXES = ['/home', '/account', '/animator'];

export function isBannerPath(path: string): boolean {
    return BANNER_PREFIXES.some((prefix) => under(path, prefix));
}

export function bannerAllowed(input: ScopeInput): boolean {
    return analyticsAllowed(input) && isBannerPath(input.path);
}

/**
 * Comptes créés AVANT la mise en place du bandeau : accord réputé acquis pour les deux finalités
 * (décision Victorien du 20/10/2026, risque CNIL assumé — wiki memory/decisions/2026-10-20-*). Ils voient
 * un bandeau d'information avec « Refuser » aussi visible que les autres boutons, et peuvent changer
 * d'avis dans Mon compte. Date de création inconnue → traité comme un nouveau compte (on demande).
 */
export const LEGACY_ACCOUNTS_BEFORE_MS = Date.parse('2026-10-21T00:00:00+02:00');

export function isLegacyAccount(creationTime: string | undefined | null): boolean {
    const created = creationTime ? Date.parse(creationTime) : NaN;
    return Number.isFinite(created) && created < LEGACY_ACCOUNTS_BEFORE_MS;
}
